import type { QueryClient } from "@tanstack/react-query";
import type { AppAbility, CustomerDto, User } from "@yalana/contracts";
import { buildAbility, buildAbilityFromLegacy } from "@yalana/contracts";
import {
  authClient,
  projectRef,
} from "../../shared/infrastructure/auth-client";
import { invalidateToken } from "../../shared/infrastructure/api-client";

export function abilityFromUser(user: User | null): AppAbility {
  if (!user) return buildAbilityFromLegacy([], false);
  if (user.permissionRules !== undefined)
    return buildAbility(user.permissionRules);
  return buildAbilityFromLegacy(
    user.permissions ?? [],
    user.platformAdmin === true,
  );
}

export interface AuthContextValue {
  user: User | null;
  ability: AppAbility;
  authorizedTenants: CustomerDto[];
  loading: boolean;
  signInWithPassword: (credentials: {
    email: string;
    password: string;
  }) => Promise<User>;
  signInWithOtp: (email: string) => Promise<void>;
  signUpWithMagicLink: (credentials: {
    email: string;
    name: string;
  }) => Promise<void>;
  signOut: () => Promise<void>;
}

const CACHE_KEY = `yalana:auth:user:${projectRef}`;

export function readCache(): { user: User | null } {
  try {
    const rawUser = localStorage.getItem(CACHE_KEY);
    return {
      user: rawUser ? (JSON.parse(rawUser) as User) : null,
    };
  } catch {
    return { user: null };
  }
}

export function writeCache(user: User | null): void {
  if (user) {
    localStorage.setItem(CACHE_KEY, JSON.stringify(user));
  } else {
    localStorage.removeItem(CACHE_KEY);
  }
}

export function composeAuthValue(
  user: User | null,
  authorizedTenants: CustomerDto[],
  loading: boolean,
  sync: (hasSession: boolean) => Promise<User | null>,
  queryClient: QueryClient,
): AuthContextValue {
  return {
    user,
    ability: abilityFromUser(user),
    authorizedTenants,
    loading,
    signInWithPassword: async (creds) => {
      const { error } = await authClient.signIn.email(creds);
      if (error) throw error;
      const appUser = await sync(true);
      if (!appUser) throw new Error("Failed to resolve user");
      return appUser;
    },
    signInWithOtp: async (_email) => {
      throw new Error("OTP sign-in is not enabled");
    },
    signUpWithMagicLink: async (_creds) => {
      throw new Error("Magic-link sign-up is not enabled");
    },
    signOut: async () => {
      await authClient.signOut();
      queryClient.clear();
      invalidateToken();
      writeCache(null);
      window.location.href = "/login";
    },
  };
}
